// src/components/admin/flows/canvas/WorkflowCanvas.js
import React, { useEffect, useCallback } from 'react';
import { useFlowAutomation, generateId } from '../../../../contexts/FlowAutomationContext';
import { useTheme } from '../../../../contexts/ThemeContext';
import useCanvasZoom from './useCanvasZoom';
import useDragNode from './useDragNode';
import useConnectNodes from './useConnectNodes';
import CanvasNode from './CanvasNode';
import CanvasConnection from './CanvasConnection';
import ConnectionLine from './ConnectionLine';
import CanvasControls from './CanvasControls';

const WorkflowCanvas = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const {
        currentWorkflow,
        selectedNodeId,
        setSelectedNodeId,
        selectedConnectionId,
        setSelectedConnectionId,
        updateStepPosition,
        addConnection,
        removeStep,
        removeConnection,
    } = useFlowAutomation();

    const { zoom, pan, handleWheel, zoomIn, zoomOut, fitToScreen, startPan, updatePan, endPan } = useCanvasZoom();

    const { startDrag, updateDrag, endDrag } = useDragNode({
        zoom,
        onPositionChange: updateStepPosition,
    });

    const {
        connectingFrom,
        mousePos,
        canvasRef,
        startConnection,
        updateConnection,
        endConnection,
        cancelConnection,
        getConnectionStart,
    } = useConnectNodes({
        zoom,
        pan,
        steps: currentWorkflow?.steps || [],
        onConnect: (connData) => {
            addConnection({
                id: generateId('conn'),
                ...connData,
            });
        },
    });

    // Handle keyboard shortcuts
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Delete' || e.key === 'Backspace') {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;

            if (selectedConnectionId) {
                removeConnection(selectedConnectionId);
            } else if (selectedNodeId) {
                const step = currentWorkflow?.steps?.find(s => s.id === selectedNodeId);
                if (step && step.type !== 'trigger') {
                    removeStep(selectedNodeId);
                }
            }
        }
        if (e.key === 'Escape') {
            cancelConnection();
            setSelectedNodeId(null);
            setSelectedConnectionId(null);
        }
    }, [selectedNodeId, selectedConnectionId, currentWorkflow, removeStep, removeConnection, cancelConnection, setSelectedNodeId, setSelectedConnectionId]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const steps = currentWorkflow?.steps || [];
    const connections = currentWorkflow?.connections || [];

    const handleCanvasClick = (e) => {
        // Only deselect if clicking the canvas background itself
        if (e.target === e.currentTarget || e.target.closest('[data-canvas-bg]')) {
            setSelectedNodeId(null);
            setSelectedConnectionId(null);
            cancelConnection();
        }
    };

    const handleNodeSelect = (nodeId) => {
        setSelectedNodeId(nodeId);
        setSelectedConnectionId(null);
    };

    const handleConnectionSelect = (connId) => {
        setSelectedConnectionId(connId);
        setSelectedNodeId(null);
    };

    const connectionStart = getConnectionStart();

    return (
        <div
            ref={canvasRef}
            className={`relative flex-1 overflow-hidden rounded-xl border ${
                isDark ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
            }`}
            onWheel={handleWheel}
            onPointerDown={(e) => {
                startPan(e);
                if (connectingFrom && !e.target.closest('[data-port]')) {
                    cancelConnection();
                }
            }}
            onPointerMove={(e) => {
                updatePan(e);
                updateDrag(e);
                updateConnection(e);
            }}
            onPointerUp={(e) => {
                endPan();
                endDrag();
                if (connectingFrom) {
                    endConnection(e);
                }
            }}
            onClick={handleCanvasClick}
        >
            {/* Grid pattern background */}
            <svg data-canvas-bg className="absolute inset-0 w-full h-full pointer-events-none">
                <defs>
                    <pattern
                        id="grid"
                        width={20 * zoom}
                        height={20 * zoom}
                        patternUnits="userSpaceOnUse"
                        patternTransform={`translate(${pan.x % (20 * zoom)}, ${pan.y % (20 * zoom)})`}
                    >
                        <circle
                            cx={1}
                            cy={1}
                            r={0.8}
                            fill={isDark ? '#374151' : '#d1d5db'}
                        />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>

            {/* SVG layer for connections only */}
            <svg
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ overflow: 'visible', zIndex: 1 }}
            >
                <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
                    {/* Connections */}
                    {connections.map((conn) => (
                        <CanvasConnection
                            key={conn.id}
                            connection={conn}
                            steps={steps}
                            isSelected={selectedConnectionId === conn.id}
                            onSelect={handleConnectionSelect}
                            isDark={isDark}
                        />
                    ))}

                    {/* Temporary connection line */}
                    {connectingFrom && mousePos && connectionStart && (
                        <ConnectionLine
                            start={connectionStart}
                            end={mousePos}
                            isDark={isDark}
                        />
                    )}
                </g>
            </svg>

            {/* HTML layer for nodes — rendered as positioned divs for reliable click handling */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0,
                    transformOrigin: '0 0',
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    pointerEvents: 'none',
                    zIndex: 2,
                }}
            >
                <div style={{ position: 'relative', pointerEvents: 'auto' }}>
                    {steps.map((step) => (
                        <CanvasNode
                            key={step.id}
                            step={step}
                            isSelected={selectedNodeId === step.id}
                            onSelect={handleNodeSelect}
                            onDragStart={startDrag}
                            onPortMouseDown={startConnection}
                            onPortMouseUp={(e, targetId) => endConnection(e, targetId)}
                            isDark={isDark}
                        />
                    ))}
                </div>
            </div>

            {/* Controls */}
            <CanvasControls
                zoom={zoom}
                onZoomIn={zoomIn}
                onZoomOut={zoomOut}
                onFitToScreen={fitToScreen}
            />

            {/* Helper text */}
            {steps.length <= 1 && (
                <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none ${
                    isDark ? 'text-gray-600' : 'text-gray-400'
                }`}>
                    <p className="text-sm font-medium">Click nodes in the palette to add them</p>
                    <p className="text-xs mt-1">Connect nodes by dragging from output to input ports</p>
                </div>
            )}
        </div>
    );
};

export default WorkflowCanvas;
